import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/sharp-light-svg-icons";
import Modal from "components/Modal/Modal";
import styles from "./BrokerageConnectIssue.module.scss";

export type ErrorType =
  | "size"
  | "upload"
  | "config"
  | "duplicate"
  | "default"
  | "expired"
  | "fraud"
  | "name"
  | "known";

type Props = {
  errorType?: ErrorType;
  closeTitle: () => void;
  visible: boolean;
};

const BrokerageConnectIssue = (props: Props) => {
  const getErrorMessage = (type?: ErrorType) => {
    const emailLink = (
      <a href="mailto:service@tiicker.com">service@tiicker.com</a>
    );

    switch (type) {
      case "size":
        return (
          <>
            The file size of a pdf upload is limited to 5mb. Please reduce the
            file size and try again. <br /> <br /> If you believe this is an
            error or mistake or if you need assistance, please{" "}
            <a href="mailto:service@tiicker.com">
              contact the TiiCKER support team
            </a>
            .
          </>
        );
      case "expired":
        return (
          <>
            The brokerage statement you provided is out of date. Please provide
            a statement from within the last 30 days. <br /> <br /> If you need
            assistance, please contact {emailLink}.
          </>
        );
      case "upload":
        return (
          <>
            It seems there was a problem with your upload. Please try again. If
            the problem persists, please contact {emailLink} for assistance.
          </>
        );
      case "config":
        return (
          <>
            The document you provided does not appear to be a brokerage
            statement, or is missing some required information. Please check the
            file and try again. If the problem persists, please contact{" "}
            {emailLink} for assistance.
          </>
        );
      case "fraud":
        return (
          <>
            It seems there was a problem with your upload. Please try again. If
            the problem persists, please contact {emailLink} for assistance.
          </>
        );
      case "duplicate":
        return (
          <>
            It seems this statement has already been uploaded or associated with
            a TiiCKER account. Please note previously uploaded statements to not
            need to be uploaded again. <br /> <br /> If you believe this error
            was a mistake, please contact the
            <a href="mailto:service@tiicker.com">TiiCKER support team</a>.
          </>
        );
      case "name":
        return (
          <>
            The name on the document doesn't match your user. Please make sure
            the name on the document and the user name are consistent before
            uploading. If you feel you are getting this message in error, please
            contact {emailLink} for assistance.
          </>
        );
      case "known":
        return (
          <>
            TiiCKER does not currently recognize your brokerage statement.
            Please give our technical support team 24-48 hours to review this
            issue. Feel free to connect your brokerage directly to our secure
            verification platform in the meantime. If you feel you are getting
            this message in error, please contact the{" "}
            <a href="mailto:service@tiicker.com">TiiCKER support team</a>. .
          </>
        );
      default:
        return (
          <>
            It seems there was a problem with your upload. Please try again. If
            the problem persists, please contact {emailLink} for assistance.
          </>
        );
    }
  };

  const renderContent = () => (
    <div className={styles.BrokerageConnectIssue}>
      <FontAwesomeIcon
        onClick={props.closeTitle}
        icon={faXmark}
        className={styles.BrokerageConnectIssue__close}
      />
      <div className={styles.BrokerageConnectIssue__iconBackground}>
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className={styles.BrokerageConnectIssue__icon}
        />
      </div>

      <div>
        <h3 className={styles.BrokerageConnectIssue__title}>
          Brokerage statement upload issue
        </h3>
        <p className={styles.BrokerageConnectIssue__message}>
          {getErrorMessage(props.errorType)}
        </p>
      </div>
    </div>
  );

  const renderMobileContent = () => (
    <div className={styles.BrokerageConnectIssue__mobileContent}>
      <div className={styles.BrokerageConnectIssue__mobileIconContainer}>
        <div className={styles.BrokerageConnectIssue__iconBackground}>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className={styles.BrokerageConnectIssue__icon}
          />
        </div>
      </div>

      <h3 className={styles.BrokerageConnectIssue__mobileTitle}>
        Brokerage statement upload issue
      </h3>

      <p className={styles.BrokerageConnectIssue__mobileMessage}>
        {getErrorMessage(props.errorType)}
      </p>
    </div>
  );

  // Desktop view
  if (window.innerWidth > 768) {
    return renderContent();
  }

  // Mobile view with modal
  return (
    <Modal
      visible={props.visible}
      onClose={props.closeTitle}
      modalClassName={styles.BrokerageConnectIssue__modal}
    >
      {renderMobileContent()}
    </Modal>
  );
};

export default BrokerageConnectIssue;
